import * as _ from 'lodash';
import {ApiUkShift, ApiUkPayFactor} from '../../../external-api/models/timecard-api.uk.shifts.model';
import {IUkBreakage, UkBreakages, UkPayFactors} from '../../../../common/timecard-models/uk.model';

/**
 * Maps UK breakage pay factors from API format to internal format
 * @param breakagePayFactors - Array of API UK pay factors
 * @param omitUndefinedPropValues - Whether to omit undefined property values
 * @returns Mapped UK pay factors object keyed by pay code
 */
export const MapUkBreakagePayFactors = (breakagePayFactors: ApiUkPayFactor[], omitUndefinedPropValues: boolean = false): UkPayFactors => {
  return _
    .chain(breakagePayFactors)
    .map(({
        amount,
        breakageOrdinal,
        duration,
        isFlat,
        originalDuration,
        payCode,
        payCodeName,
        payDescription,
        payFactor,
        payFactorName,
        rate,
        effectiveRate
    }: ApiUkPayFactor, index) => {
      const mappedPayFactor: UkPayFactors[string] = {
        amount,
        breakageOrdinal: breakageOrdinal ?? index + 1,
        duration: {value: duration},
        isFlat,
        payCode,
        payCodeName,
        payDescription,
        payFactor,
        payFactorName,
        rate,
        originalDuration,
        effectiveRate
      };
      if (omitUndefinedPropValues) {
        const propertiesToCheck = ['originalDuration', 'rate'];
        propertiesToCheck.forEach(prop => {
          if (!mappedPayFactor[prop]) {
            delete mappedPayFactor[prop];
          }
        });
      }
      return mappedPayFactor;
    })
    .keyBy('payCode')
    .value();
};

/**
 * Maps UK breakages from API format to internal format
 * @param apiShifts - Array of API UK shifts
 * @param omitUndefinedPropValues - Whether to omit undefined property values
 * @returns Mapped UK breakages object keyed by shift ID
 */
export const MapUkBreakages = (apiShifts: ApiUkShift[], omitUndefinedPropValues: boolean = false): UkBreakages => {
  return _
    .chain(apiShifts)
    .map((apiShift: ApiUkShift): {shiftId: string, breakages: IUkBreakage} => {
      const {
        timecardShiftId: shiftId = '',
        breakageHoursWorked: hoursWorked = 0,
        breakageTotalAmount: totalAmount = 0,
        originalBreakageTotalAmount: originalTotalAmount = 0,
        hasBrokenTurnaround,
        prepWrapDuration,
        compensatoryRestDuration,
        breakagePayFactors,
      } = apiShift;
      const breakage: IUkBreakage = {
        shiftId,
        hoursWorked,
        totalAmount,
        payFactors: MapUkBreakagePayFactors(breakagePayFactors, omitUndefinedPropValues),
        originalTotalAmount,
        hasBrokenTurnaround,
        compensatoryRest: compensatoryRestDuration,
        prepWrap: prepWrapDuration,
      };
      if (omitUndefinedPropValues) {
        const propertiesToCheck = ['compensatoryRest', 'prepWrap'];
        propertiesToCheck.forEach(prop => {
          if (!breakage[prop]) {
            delete breakage[prop];
          }
        });
      }
      return {
        shiftId,
        breakages: breakage
      };
    })
    .keyBy('shiftId')
    .mapValues('breakages')
    .value();
};
